import { uniqWith } from "lodash";
import queryString from "query-string";

import Sentry from "@similarweb/sentry-logger";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import { multiIncludeExcludeType } from "app/components/multi-include-exclude-filter/consts";
import { MultiIncludeExcludeFilterContainer } from "app/components/multi-include-exclude-filter/MultiIncludeExcludeFilterContainer";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { numberFilter, minVisitsAbbrFilter } from "app/filters/ngFilters";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import {
    GOOGLE_KEYWORD_GENERATOR_ENDPOINT,
    GOOGLE_KEYWORD_GENERATOR_EXCEL_ENDPOINT,
    TABLE_SELECTION_KEY,
    TABLE_SELECTION_PROPERTY,
} from "app/common/keyword-research/keyword-generator/constants";
import { KeywordGeneratorToolPageModal } from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/KeywordGeneratorToolPageModal";
import { ChipdownItem } from "app/pages/website-analysis/audience-interests/single/AudienceInterestsTableTop/AudienceInterestsTableTopStyles";
import { CpcFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/CpcFilter";
import {
    KeywordDifficultyFilter,
    WEBSITE_KEYWORDS_PAGE_FILTERS_CONSTANTS,
} from "app/pages/website-analysis/traffic-sources/search/components/filters/KeywordDifficultyFilter";
import { KeywordIntent } from "app/pages/website-analysis/traffic-sources/search/components/filters/KeywordIntent";
import { VolumeFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/VolumeFilter";
import { WebsiteKeywordsPageTableTopContextProvider } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import { getWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageUtillities";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import DurationService from "app/services/DurationService";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsQueryClient, keywordGroupsTransforms } from "app/services/keyword-groups";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import {
    createCpcFilter,
    createDifficultyValue,
    createVolumeFilter,
    getRangeFilterQueryParamValue,
} from "pro-features/components/filtersPanel/src/RangeFilterUtilityFunctions";
import {
    TableSelectionGroupKeywordComponent,
    generalGroupToastMessage,
} from "pro-features/components/TableSelection/src/TableSelectionGroupKeywordComponent";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { RootState } from "single-spa/store/types";

import { TableSelection } from "pro-features/components/TableSelection/src/TableSelection";
import { ETableSelectionNewGroupDropdownMode } from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import { tableActionsCreator } from "../../../../../../actions/tableActions";
import { SWReactTableWrapperContextConsumer } from "../../../../../../components/React/Table/SWReactTableWrapperSelectionContext";
import { SWReactTableWrapperWithSelection } from "../../../../../../components/React/Table/SWReactTableWrapperWithSelection";
import { allTrackers } from "../../../../../../services/track/track";
import { DownloadExcelContainer } from "../../../../../workspace/StyledComponent";

import { KeywordGeneratorToolPageTableAbstract } from "./KeywordGeneratorToolPageTableAbstract";
import KeywordGeneratorToolPageTableHeader from "./KeywordGeneratorToolPageTableHeader";
import {
    FiltersRowContainer,
    TableHeaderItemContainer,
    TableHeaderItemsContainer,
    TableHeaderText,
} from "./styledComponents";

const KEYWORD_ANALYSIS_STATE = "organicsearch_keywordAnalysis_2";
const KEYWORD_OVERVIEW_STATE = "organicsearch_keywordAnalysis_overview_2";

const TableHeaderItem = ({ title, subtitle }: { title: string; subtitle: string }) => {
    return (
        <TableHeaderItemContainer>
            <TableHeaderText
                weight={500}
                size={18}
            >
                {title}
            </TableHeaderText>
            <TableHeaderText>{subtitle}</TableHeaderText>
        </TableHeaderItemContainer>
    );
};

interface IKeywordGeneratorToolPageTableBaseProps {
    multiIncludeExcludeKeywords: string;
    seedKeyword: string;
    isGroupContext: boolean;
    webSource: string;
    country: number;
    duration: string;
    cpcFromValue: string;
    cpcToValue: string;
    volumeFromValue: string;
    volumeToValue: string;
    difficultyFromValue: string;
    intentsfilter: string;
    difficultyToValue: string;
    clearAllSelectedRows: () => void;
    showToast: (href: string, text: string, label: string) => void;
    notify?: boolean;
    tableSelection: any; // artem: the real any here
    isTableInView: boolean;
    getDataCallback?: (data) => void;
    onDataError?: (err) => void;
    onKeywordsAddedToGroup: (group: IUserKeywordGroup, newlyCreatedGroup: boolean) => void;
    selectRows?: boolean;
    selectAllRows: (rows) => void;
}

interface IKeywordGeneratorToolPageTableBaseState {
    totalVisits: number;
    totalClicks: number;
    totalVolume: number;
    totalRows: number;
    isTableSelectionLoading: boolean;
    tableSelectionIsValid: boolean;
    tableSelectionErrorMessage: string;
    tableSelectionMode: ETableSelectionNewGroupDropdownMode;
    isSuccessModalOpen: boolean;
    isSuccessModalNavigateButtonVisible: boolean;
    isCreateNewListModalOpen: boolean;
    groupName: string;
    newlyCreatedGroup: boolean;
    tableSelectionCurrentGroup: string;
    orderBy: {
        field: string;
        sortDirection: string;
    };
    number: number;
    error: {
        status: number;
        name: {
            Keyword: string[];
        };
    };
    keywordGroups: IUserKeywordGroup[];
    isKeywordGroupsLoading: boolean;
}

export class KeywordGeneratorToolPageTableBase extends KeywordGeneratorToolPageTableAbstract<
    IKeywordGeneratorToolPageTableBaseProps,
    IKeywordGeneratorToolPageTableBaseState
> {
    public static defaultProps = {
        notify: false,
    };

    protected swNavigator = proNavigator;

    protected swSettings: typeof swSettings;

    protected i18nFilter: ReturnType<typeof i18nFilter>;

    protected numberFilter = numberFilter();

    protected visitNumberFilter = minVisitsAbbrFilter();

    protected dropdownRef;

    protected maxKeywordsInGroup = KeywordGroupEditorHelpers.getMaxGroupCount();

    protected tableSelectionKey = "KeywordGeneratorToolTable";

    protected tableSelectionProperty = "keyword";

    protected tableSettings;

    protected tableStorageKey;

    protected dropdownAppendTo;

    protected transformData;

    protected dataParamsAdapter;

    protected pageIndent = 1;

    protected isTrial: boolean;

    constructor(props) {
        super(props);
        this.swSettings = swSettings;
        this.i18nFilter = i18nFilter();
        this.isTrial = isTrialOrAfterTrial();
        this.state = {
            totalVolume: 0,
            totalVisits: 0,
            totalClicks: 0,
            totalRows: 0,
            tableSelectionCurrentGroup: null,
            tableSelectionErrorMessage: null,
            isTableSelectionLoading: false,
            tableSelectionIsValid: true,
            isSuccessModalOpen: false,
            isSuccessModalNavigateButtonVisible: true,
            newlyCreatedGroup: false,
            isCreateNewListModalOpen: false,
            groupName: "",
            tableSelectionMode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
            orderBy: {
                field: "",
                sortDirection: "",
            },
            number: 0,
            error: null,
            keywordGroups: [],
            isKeywordGroupsLoading: false,
        };
    }

    public UNSAFE_componentWillMount(): void {
        this.setState({
            orderBy: {
                field: this.tableSettings.defaultSortField(this.props.duration),
                sortDirection: this.tableSettings.defaultSortDirection,
            },
        });
        void this.loadKeywordGroups();
    }

    private loadKeywordGroups = async () => {
        try {
            this.setState({ isKeywordGroupsLoading: true });
            const { userGroups, sharedGroups } = await keywordGroupsQueryClient.getAllGroups();
            this.setState({
                keywordGroups: [...userGroups, ...sharedGroups],
                isKeywordGroupsLoading: false,
            });
        } catch (error) {
            Sentry.error("Error loading keyword groups", error);
            this.setState({
                keywordGroups: [],
                isKeywordGroupsLoading: false,
            });
        }
    };

    private getGroupById = (groupId: string): IUserKeywordGroup | undefined => {
        return this.state.keywordGroups.find(({ Id }) => Id === groupId);
    };

    private getGroupByName = (groupName: string): IUserKeywordGroup | undefined => {
        return this.state.keywordGroups.find(({ Name }) => Name.toLowerCase() === groupName.toLowerCase());
    };

    public getColumns(filters) {
        const columns = this.tableSettings.getColumns(
            {
                field: filters.sort,
                sortDirection: filters.asc ? "asc" : "desc",
            },
            filters.websource,
        );
        return columns;
    }

    public getNoDataTitle() {
        switch (this.state.error?.status) {
            case 400:
                switch (this.state.error.name?.Keyword?.[0]) {
                    case "long_phrase":
                        return "keyword.generator.tool.table.nodata.input.too.long.title";
                    case "short_phrase":
                        return "keyword.generator.tool.table.nodata.input.too.short.title";
                    default:
                        return "keyword.generator.tool.table.nodata.title";
                }
            default:
                return "keyword.generator.tool.table.nodata.title";
        }
    }

    public getNoDataSubtitle() {
        switch (this.state.error?.status) {
            case 400:
                switch (this.state.error.name?.Keyword?.[0]) {
                    case "long phrase":
                        return "keyword.generator.tool.table.nodata.input.too.long.subtitle";
                    case "short phrase":
                        return "keyword.generator.tool.table.nodata.input.too.short.subtitle";
                    default:
                        return "keyword.generator.tool.table.nodata.subtitle";
                }
            default:
                return "keyword.generator.tool.table.nodata.subtitle";
        }
    }

    public render() {
        const { filters, requestBody } = this.getTableFilters();
        const columns = this.tableSettings.getColumns(
            {
                field: filters.sort,
                sortDirection: filters.asc ? "asc" : "desc",
            },
            filters.websource,
            filters.country,
            this.props.duration,
        );

        return (
            <>
                <SWReactTableWrapperWithSelection
                    pageIndent={this.pageIndent}
                    rowsPerPage={100}
                    preventCountTracking={!this.props.isTableInView}
                    preventRender={!this.props.isTableInView}
                    allowClientSort={true}
                    tableSelectionKey={this.tableSelectionKey}
                    tableSelectionProperty={this.tableSelectionProperty}
                    getDataCallback={this.getDataCallback}
                    cleanOnUnMount={false}
                    serverApi={GOOGLE_KEYWORD_GENERATOR_ENDPOINT}
                    onSort={this.onSort}
                    tableColumns={columns}
                    initialFilters={filters}
                    requestBody={requestBody}
                    dataParamsAdapter={this.dataParamsAdapter}
                    transformData={this.transformData}
                    onDataError={this.onDataError}
                    tableOptions={{
                        noDataTitle: i18nFilter()(this.getNoDataTitle()),
                        noDataSubtitle: i18nFilter()(this.getNoDataSubtitle()),
                        metric: this.tableStorageKey,
                        tableSelectionTrackingParam: "keyword",
                        aboveHeaderComponents: [
                            this.getTableSelectionComponent(this.getDropdown(this.dropdownAppendTo)),
                        ],
                    }}
                >
                    {(topComponentProps) => (
                        <KeywordGeneratorToolPageTableHeader
                            volumeFromValue={this.props.volumeFromValue}
                            volumeToValue={this.props.volumeToValue}
                            cpcFromValue={this.props.cpcFromValue}
                            cpcToValue={this.props.cpcToValue}
                            difficultyFromValue={this.props.difficultyFromValue}
                            intentsfilter={this.props.intentsfilter}
                            difficultyToValue={this.props.difficultyToValue}
                            {...topComponentProps}
                            getDurationApiParams={this.getDurationApiParams}
                        >
                            {this.getTableHeaderChildren(
                                topComponentProps.tableColumns,
                                topComponentProps.onClickToggleColumns,
                                topComponentProps.totalCount,
                            )}
                        </KeywordGeneratorToolPageTableHeader>
                    )}
                </SWReactTableWrapperWithSelection>
                <KeywordGeneratorToolPageModal
                    isOpen={this.state.isSuccessModalOpen}
                    onCloseClick={this.onModalClose}
                    onViewGroupClick={
                        this.state.isSuccessModalNavigateButtonVisible ? this.onViewGroupClick : undefined
                    }
                    onStartOverClick={this.onStartOverClick}
                    newlyCreatedGroup={this.state.newlyCreatedGroup}
                />
            </>
        );
    }

    private onModalClose = () => {
        this.setState({ isSuccessModalOpen: false, isSuccessModalNavigateButtonVisible: true });
    };

    private onStartOverClick = () => {
        this.props.clearAllSelectedRows();
        this.setState({ isSuccessModalOpen: false, isSuccessModalNavigateButtonVisible: true });
    };

    private onViewGroupClick = () => {
        const { country, duration, webSource } = this.swNavigator.getParams();
        const { groupName } = this.state;
        this.swNavigator.go("organicsearch_keywordAnalysis_2", {
            country,
            duration,
            keyword: `*${groupName}`,
        });
    };

    protected onDataError = (err) => {
        if (typeof this.props.onDataError === "function") {
            this.props.onDataError(err);
        }

        this.setState({ error: err });
    };

    protected getTableFilters = () => {
        const durationObject = this.getDurationApiParams(this.props.duration);
        const rangeFilter = getRangeFilterQueryParamValue([
            createVolumeFilter(this.props.volumeFromValue, this.props.volumeToValue, "volume"),
            createCpcFilter(this.props.cpcFromValue, this.props.cpcToValue, "cpc"),
            createDifficultyValue(this.props.difficultyFromValue, this.props.difficultyToValue),
        ]);
        const filters: { [key: string]: string | boolean | number } = {
            keyword: this.props.seedKeyword,
            country: this.props.country,
            from: durationObject.forAPI.from,
            to: durationObject.forAPI.to,
            isWindow: durationObject.forAPI.isWindow,
            websource: this.props.webSource,
            sort: this.state.orderBy.field,
            asc: this.state.orderBy.sortDirection === "asc",
            rangeFilter,
            intentsfilter: this.props.intentsfilter,
            rowsPerPage: 100,
        };

        const { seedKeyword, isGroupContext } = this.props;

        if (isGroupContext && seedKeyword) {
            const keywordsGroup = this.getGroupById(seedKeyword) || this.getGroupByName(seedKeyword);
            if (keywordsGroup) {
                filters.GroupHash = keywordsGroup.GroupHash;
                filters.keyword = keywordsGroup.Id;
            }
        }

        const requestBody = this.props.multiIncludeExcludeKeywords
            ? JSON.parse(this.props.multiIncludeExcludeKeywords)
            : [];

        return { filters, requestBody };
    };

    private getColumnsPickerLiteProps = (
        tableColumns,
        onColumnToggle,
    ): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle: onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };

    private contextValue = () =>
        getWebsiteKeywordsPageTableTopContext({
            initialFiltersStateObject: { current: {} },
            nextTableParams: {
                cpcFromValue: this.props.cpcFromValue,
                cpcToValue: this.props.cpcToValue,
                volumeFromValue: this.props.volumeFromValue,
                volumeToValue: this.props.volumeToValue,
                difficultyFromValue: this.props.difficultyFromValue,
                difficultyToValue: this.props.difficultyToValue,
                intentsfilter: this.props.intentsfilter,
            },
            tableData: {},
            addTempParams: (values) => {
                proNavigator.updateParams(values);
            },
        });

    protected getTableHeaderChildren = (columns, onClickToggleColumns, totalCount) => {
        const excelLinkAndBody = !this.isTrial
            ? this.getExcelLinkAndBody()
            : { excelDownloadUrl: "", excelBodyForReq: null };
        const isTotalDefined = this.state.totalVolume !== undefined;
        return (
            <>
                {this.state.totalRows > 0 ? (
                    <TableHeaderItemsContainer>
                        <TableHeaderItem
                            title={this.numberFilter(this.state.totalRows)}
                            subtitle={this.i18nFilter("keyword.generator.tool.page.table.header.total.rows")}
                        />
                        <TableHeaderItem
                            title={this.visitNumberFilter(
                                isTotalDefined ? this.state.totalVolume : this.state.totalVisits,
                            )}
                            subtitle={this.i18nFilter(
                                isTotalDefined
                                    ? "keyword.generator.tool.page.table.header.total.volume"
                                    : "keyword.generator.tool.page.table.header.total.visits",
                            )}
                        />
                    </TableHeaderItemsContainer>
                ) : null}
                <WebsiteKeywordsPageTableTopContextProvider value={this.contextValue()}>
                    <FiltersRowContainer alignItems="center">
                        <FlexRow>
                            <ChipdownItem>
                                <MultiIncludeExcludeFilterContainer filterType={multiIncludeExcludeType.KEYWORD} />
                            </ChipdownItem>
                            <ChipdownItem>
                                <CpcFilterForWebsiteKeywords />
                            </ChipdownItem>
                            <ChipdownItem>
                                <VolumeFilterForWebsiteKeywords />
                            </ChipdownItem>
                            <ChipdownItem>
                                <KeywordDifficultyFilter
                                    rangeLimits={WEBSITE_KEYWORDS_PAGE_FILTERS_CONSTANTS.RAGE_LIMITS}
                                    setNewDifficultyRange={this.contextValue().setNewDifficultyRange}
                                    difficultyFromValue={Number(this.props.difficultyFromValue)}
                                    difficultyToValue={Number(this.props.difficultyToValue)}
                                />
                            </ChipdownItem>
                            <ChipdownItem>
                                <KeywordIntent
                                    intentsfilter={this.props.intentsfilter}
                                    onIntentChange={this.contextValue().onIntentChange}
                                />
                            </ChipdownItem>
                        </FlexRow>
                        <FlexRow>
                            <DownloadExcelContainer>
                                <DownloadButtonMenu
                                    Excel={true}
                                    PaidExcel={isPaidExcelAllowed()}
                                    totalCount={totalCount}
                                    downloadUrl={excelLinkAndBody.excelDownloadUrl}
                                    excelBodyForReq={excelLinkAndBody.excelBodyForReq}
                                    exportFunction={this.trackExcelDownload}
                                    excelLocked={this.isTrial}
                                />
                            </DownloadExcelContainer>
                            <ColumnsPickerLite
                                {...this.getColumnsPickerLiteProps(columns, onClickToggleColumns)}
                                withTooltip
                                translations={{
                                    tooltip: this.i18nFilter("workspace.sales.column.picker"),
                                    getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                        this.i18nFilter("common.columnsPicker.selectedAmount", {
                                            selected: selectedValue,
                                            total: totalValue,
                                        }),
                                    mandatoryField: this.i18nFilter("common.columnsPicker.mandatoryField"),
                                    apply: this.i18nFilter("common.apply"),
                                }}
                            />
                        </FlexRow>
                    </FiltersRowContainer>
                </WebsiteKeywordsPageTableTopContextProvider>
            </>
        );
    };

    protected trackExcelDownload = () => {
        allTrackers.trackEvent("Download", "submit-ok", "Table/Download Excel");
    };

    protected getExcelLinkAndBody = () => {
        const allTableFilters = this.getTableFilters();
        const requestBody = allTableFilters.requestBody;
        let tableFilters = allTableFilters.filters;

        delete tableFilters.rowsPerPage;

        if (this.dataParamsAdapter) {
            tableFilters = this.dataParamsAdapter(tableFilters);
        }
        const params = queryString.stringify(tableFilters);
        return {
            excelDownloadUrl: `${GOOGLE_KEYWORD_GENERATOR_EXCEL_ENDPOINT}?${params}`,
            excelBodyForReq: requestBody,
        };
    };

    protected getDataCallback = (data) => {
        this.setState({
            totalVisits: data.totalVisits,
            totalClicks: data.totalClicks,
            totalRows: data.totalRecords,
            totalVolume: data.totalVolume,
        });
        if (typeof this.props.getDataCallback === "function") {
            this.props.getDataCallback(data);
        }
    };

    protected isRowLimitExceeded = () => this.getSelectedRowsFromStore().length > this.maxKeywordsInGroup;

    protected tableSelectionOnSubmit = async (name) => {
        // prevent multiple submit click
        if (this.state.isTableSelectionLoading) {
            return;
        }
        const { isValid, errorMessage } = await KeywordGroupEditorHelpers.validateTitle(name);
        if (!isValid) {
            this.setState({
                tableSelectionIsValid: isValid,
                tableSelectionErrorMessage: errorMessage,
            });
        } else {
            await this.toggleLoading();
            const selectedRowsFromStore = this.isRowLimitExceeded()
                ? this.getSelectedRowsFromStore().slice(0, this.maxKeywordsInGroup)
                : this.getSelectedRowsFromStore();
            const group = KeywordGroupEditorHelpers.keywordGroupFromList({
                title: name,
                items: selectedRowsFromStore.map((keyword) => ({ text: keyword })),
            });
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `Create New Keyword List/Save/${name || ""}/${group.Keywords.length.toString() || ""}`,
            );

            const createdGroup = group.Id
                ? await keywordGroupsQueryClient.updateGroup(group)
                : await keywordGroupsQueryClient.createGroup(group);
            if (createdGroup) {
                if (this.props.notify && createdGroup) {
                    this.setState({ groupName: createdGroup.Id });
                }
                await this.toggleLoading();
                this.props.clearAllSelectedRows();
                this.setState({
                    tableSelectionMode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
                    tableSelectionIsValid: true,
                    tableSelectionErrorMessage: null,
                    isCreateNewListModalOpen: false,
                    isSuccessModalOpen: true,
                    newlyCreatedGroup: true,
                    isSuccessModalNavigateButtonVisible: !!createdGroup,
                });
                this.dropdownRef.close();
                this.props.onKeywordsAddedToGroup(
                    {
                        ...group,
                        ...(createdGroup ?? {}),
                    },
                    true,
                );
            } else {
                // error
                this.dropdownRef.close();
            }
        }
    };

    protected toggleLoading = async () => {
        return this.setStateAsync({
            isTableSelectionLoading: !this.state.isTableSelectionLoading,
        });
    };

    protected getSelectedRowsFromStore = () => {
        const state: Partial<RootState> = { tableSelection: this.props.tableSelection };
        return getTableSelectedItems("KeywordGeneratorToolTable")(state).map((item) => item.keyword);
    };

    protected setStateAsync = async (state) => {
        return new Promise<void>((resolve) => {
            this.setState(state, resolve);
        });
    };

    protected onExistingGroupClick = async (item) => {
        const currentGroup = await keywordGroupsQueryClient.getGroupById(item.id);
        const currentKeywords = currentGroup.Keywords.map((keyword) => {
            return { text: keyword };
        });
        const newKeywords = this.getSelectedRowsFromStore().map((keyword) => {
            return { text: keyword };
        });
        const withoutDuplications = uniqWith(
            [...currentKeywords, ...newKeywords],
            (crr, other) => crr.text === other.text,
        );
        const newKeywordsCount = withoutDuplications.length - currentKeywords.length;
        // if no new keywords was added
        await this.setStateAsync({
            tableSelectionCurrentGroup: currentGroup.Name,
        });
        if (newKeywordsCount === 0) {
            await this.setStateAsync({
                tableSelectionMode: ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS,
            });
            this.dropdownRef.openWarning();
        }
        // new keywords added
        else {
            const group = KeywordGroupEditorHelpers.keywordGroupFromList(
                {
                    title: currentGroup.Name,
                    items: withoutDuplications,
                },
                currentGroup,
            );
            // check number of keywords in group
            if (group.Keywords.length > this.maxKeywordsInGroup) {
                this.setState({
                    tableSelectionMode: ETableSelectionNewGroupDropdownMode.MAX_ITEMS,
                });
                this.dropdownRef.openWarning();
            } else {
                const createdGroup = group.Id
                    ? await keywordGroupsQueryClient.updateGroup(group)
                    : await keywordGroupsQueryClient.createGroup(group);
                if (createdGroup) {
                    this.props.clearAllSelectedRows();
                    this.setState({
                        tableSelectionMode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
                        tableSelectionIsValid: true,
                        tableSelectionErrorMessage: null,
                    });
                    this.dropdownRef.close();
                    this.props.onKeywordsAddedToGroup(group, false);
                    if (this.props.notify) {
                        this.showGroupLinkToast(
                            currentGroup.Id,
                            generalGroupToastMessage(currentGroup.Name, newKeywordsCount),
                            this.i18nFilter("table.selection.keywords.seegroup"),
                            KEYWORD_ANALYSIS_STATE,
                        );
                    }
                }
            }
        }
    };

    protected tableSelectionOnCancel = () => {
        if (this.state.tableSelectionMode === ETableSelectionNewGroupDropdownMode.NEW_GROUP) {
            allTrackers.trackEvent("add to keyword group", "click", "Create New Keyword Group/Cancel");
        }
        this.setState({
            tableSelectionMode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
            tableSelectionIsValid: true,
            tableSelectionErrorMessage: null,
            isCreateNewListModalOpen: false,
        });
    };

    protected onNewListButtonClick = () => {
        this.setState({ isCreateNewListModalOpen: true });
    };

    protected onGroupClick = async (item) => {
        // click on new group
        if (!item.id) {
            this.onNewGroupClick(item);
        }
        // click on existing group
        else {
            await this.onExistingGroupClick(item);
        }
    };

    protected onNewGroupClick = (item) => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Create New Keyword List/Save/${item.name || ""}/${this.maxKeywordsInGroup.toString() || ""}`,
        );
        if (this.getSelectedRowsFromStore().length > KeywordGroupEditorHelpers.getMaxGroupCount()) {
            this.setState({
                tableSelectionMode: ETableSelectionNewGroupDropdownMode.MAX_ITEMS,
            });
            this.dropdownRef.openWarning();
        } else {
            this.setState({
                tableSelectionMode: ETableSelectionNewGroupDropdownMode.NEW_GROUP,
            });
        }
    };

    protected setRef = (ref) => {
        this.dropdownRef = ref;
    };

    protected getDropdown = (appendTo) => {
        return (
            <TableSelectionGroupKeywordComponent
                appendTo={appendTo}
                ref={this.setRef}
                groups={keywordGroupsTransforms.toDropDown(this.state.keywordGroups)}
                onSubmit={this.tableSelectionOnSubmit}
                isLoading={this.state.isTableSelectionLoading}
                error={!this.state.tableSelectionIsValid}
                errorMessage={this.state.tableSelectionErrorMessage}
                onGroupClick={this.onGroupClick}
                onNewListButtonClick={this.onNewListButtonClick}
                mode={this.state.tableSelectionMode}
                onCancel={this.tableSelectionOnCancel}
                isCreateNewListModalOpen={this.state.isCreateNewListModalOpen}
                groupIconName="keyword-group"
                count={this.maxKeywordsInGroup}
                newGroupNameLabel={this.i18nFilter("table.selection.newgroup.keywords.title")}
                trackingCategory="add to keyword group"
                allItemsExistsMessage="table.selection.keywords.exists"
                maxItemsMessage="table.selection.keywords.max.keywords"
                alertMessage={
                    this.isRowLimitExceeded()
                        ? this.i18nFilter("table.selection.newgroup.keywords.alert.message", {
                              MaxKWs: this.maxKeywordsInGroup,
                          })
                        : null
                }
                selectedRows={this.getSelectedRowsFromStore()}
            />
        );
    };

    protected getTableSelectionComponent = (groupSelectorElement) => {
        return (
            <SWReactTableWrapperContextConsumer>
                {({ selectedRows, clearAllSelectedRows }) => {
                    const text = this.i18nFilter("keyword.generator.tool.table.keywords.selected", {
                        number: selectedRows.length.toString(),
                    });
                    return (
                        <TableSelection
                            key="1"
                            selectedText={text}
                            onCloseClick={clearAllSelectedRows}
                            addToGroupLabel=""
                            isVisible={true}
                            groupSelectorElement={groupSelectorElement}
                            showSeparator={false}
                            isDisabled={selectedRows?.length === 0}
                        />
                    );
                }}
            </SWReactTableWrapperContextConsumer>
        );
    };

    protected getUrlForKeyWord(keyword) {
        const { country, duration } = this.props;
        const state = this.swNavigator.getState(KEYWORD_OVERVIEW_STATE);
        const configId = this.swNavigator.getConfigId(state);
        const defaultParams = this.swSettings.components[configId].defaultParams;
        const defaultStateQueryParams = this.swNavigator.getState(KEYWORD_OVERVIEW_STATE).defaultQueryParams;
        return this.swNavigator.href(KEYWORD_OVERVIEW_STATE, {
            ...defaultStateQueryParams,
            ...defaultParams,
            keyword,
            country,
            duration,
        });
    }

    protected getDurationApiParams = (duration) => {
        return DurationService.getDurationData(duration, null, this.swSettings.current.componentId);
    };

    protected onSort = (sortedColumn) => {
        const { field, sortDirection } = sortedColumn;
        this.setState({ orderBy: { field, sortDirection } });
    };

    protected showGroupLinkToast = (name, text, label, state?: string) => {
        const { country, duration } = this.swNavigator.getParams();
        const linkToGroup = this.swNavigator.href(state ?? KEYWORD_OVERVIEW_STATE, {
            country,
            duration,
            keyword: `*${name}`,
        });
        this.props.showToast(linkToGroup, text, label);
    };
}

export const mapStateToPropsBase = (props) => {
    const { keywordGeneratorTool, tableSelection } = props;
    const { seedKeyword = String(), country, duration, webSource } = keywordGeneratorTool;
    const isSeedKeywordStartsWithGroupPrefix = seedKeyword?.startsWith("*");
    const isGroupContext = keywordGeneratorTool.isGroupContext || isSeedKeywordStartsWithGroupPrefix;
    return {
        seedKeyword: isSeedKeywordStartsWithGroupPrefix ? seedKeyword.substring(1) : seedKeyword,
        country,
        duration,
        webSource,
        tableSelection,
        isGroupContext,
    };
};
export const mapDispatchToPropsBase = (dispatch) => {
    return {
        clearAllSelectedRows: () => {
            dispatch(tableActionsCreator("KeywordGeneratorToolTable", "keyword").clearAllSelectedRows()); // todo
        },
        showToast: (href, text, label) => {
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text,
                        linkText: label,
                        href,
                        onClick: allTrackers.trackEvent.bind(
                            allTrackers,
                            "add to keyword group",
                            "click",
                            "internal link/keywordAnalysis.organic",
                        ),
                    }),
                ),
            );
        },
        selectAllRows: (rows) => {
            dispatch(tableActionsCreator(TABLE_SELECTION_KEY, TABLE_SELECTION_PROPERTY).selectRows(rows));
        },
    };
};
